import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Seo from "../../components/Seo";
import { blogPostingSchema, breadcrumbSchema } from "../../utils/structuredData";

const API = process.env.REACT_APP_API_URL;

export default function BlogPost() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/edolutions/blog/${slug}`)
      .then(r => setPost(r.data?.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl animate-pulse mb-3">📝</div>
        <p className="text-gray-500">{t("blogPost.loading")}</p>
      </div>
    </div>
  );

  if (error || !post) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Seo title="Article Not Found" description="This article may have been removed or the link is incorrect." path={`/blog/${slug}`} noindex />
      <div className="text-center">
        <div className="text-5xl mb-4">😕</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">{t("blogPost.notFoundTitle")}</h1>
        <p className="text-gray-500 mb-6">{t("blogPost.notFoundDesc")}</p>
        <Link to="/blog" className="bg-[#EE5F2B] hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition">
          {t("blogPost.backToBlog")}
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={post.title}
        description={post.excerpt || post.title}
        keywords={(post.tags || []).join(", ")}
        path={`/blog/${slug}`}
        image={post.image}
        type="article"
        jsonLd={[
          blogPostingSchema({
            title: post.title, description: post.excerpt || post.title, path: `/blog/${slug}`,
            image: post.image, datePublished: post.created_at, author: post.author_name,
          }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${slug}` }]),
        ]}
      />
      {/* Hero */}
      <div className="bg-gradient-to-b from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <Link to="/blog" className="text-orange-400 hover:text-orange-300 text-sm font-semibold mb-4 inline-block">
            {t("blogPost.backToBlog")}
          </Link>
          {post.category && (
            <span className="ml-3 text-[10px] font-bold bg-orange-500 text-white px-2.5 py-1 rounded-full uppercase">
              {post.category}
            </span>
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4 leading-tight" style={{ fontFamily: "'Fredoka',sans-serif" }}>
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-lg text-gray-300 mt-4 max-w-3xl">{post.excerpt}</p>
          )}
          <div className="flex items-center gap-3 mt-6">
            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-lg">
              {(post.author_name || "C")[0]}
            </div>
            <div>
              <div className="font-semibold">{post.author_name || t("blogPost.authorFallback")}</div>
              <div className="text-sm text-gray-400">
                {/* NOTE: date is formatted with a hardcoded "en-US" locale regardless of current app language — not wired to i18n, left as-is to avoid changing date formatting logic. */}
                {new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                {post.views > 0 && <span> · {post.views.toLocaleString()} {t("blogPost.views")}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Image */}
      {post.image && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-8">
          <img src={post.image} alt={post.title}
            className="w-full rounded-2xl shadow-2xl object-cover max-h-[400px]" />
        </div>
      )}

      {/* Content */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="prose prose-lg prose-gray max-w-none"
          style={{ fontSize: "17px", lineHeight: 1.8, color: "#374151" }}
          dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, "<br/>") }} />
      </article>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-8">
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, i) => (
              <span key={i} className="text-xs font-semibold bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer CTA */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-t border-orange-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-3" style={{ fontFamily: "'Fredoka',sans-serif" }}>
            {t("blogPost.ctaHeading")}
          </h2>
          <p className="text-gray-600 mb-6">{t("blogPost.ctaSub")}</p>
          <div className="flex justify-center gap-4 flex-wrap">
            <a href="/kids/signup" className="bg-[#EE5F2B] hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition shadow-lg">
              {t("blogPost.ctaButton1")}
            </a>
            <Link to="/blog" className="bg-white border border-gray-200 text-gray-700 font-bold px-6 py-3 rounded-xl transition hover:bg-gray-50">
              {t("blogPost.ctaButton2")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
